import React, { useState, useEffect } from 'react';
import { useCreateCarrierMutation, useUpdateCarrierMutation, Carrier } from '../../store/api/referencesApi';

interface CreateCarrierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCarrierCreated?: (carrier: Carrier) => void;
  carrier?: Carrier | null; // Для режима редактирования
}

const CreateCarrierModal: React.FC<CreateCarrierModalProps> = ({
  isOpen,
  onClose,
  onCarrierCreated,
  carrier = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    inn: '',
    legalAddress: '',
    postalAddress: '',
    kpp: '',
    ogrn: '',
    bankName: '',
    bankAccount: '',
    bankCorrAccount: '',
    bankBik: '',
    email: '',
    directorName: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createCarrier, { isLoading: isCreating }] = useCreateCarrierMutation();
  const [updateCarrier, { isLoading: isUpdating }] = useUpdateCarrierMutation();

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!carrier;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Наименование перевозчика обязательно для заполнения';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode && carrier) {
        const updatedCarrier = await updateCarrier({
          id: carrier.id,
          data: {
            name: formData.name.trim(),
            contactPerson: formData.contactPerson.trim() || undefined,
            phone: formData.phone.trim() || undefined,
            inn: formData.inn.trim() || undefined,
            legalAddress: formData.legalAddress.trim() || undefined,
            postalAddress: formData.postalAddress.trim() || undefined,
            kpp: formData.kpp.trim() || undefined,
            ogrn: formData.ogrn.trim() || undefined,
            bankName: formData.bankName.trim() || undefined,
            bankAccount: formData.bankAccount.trim() || undefined,
            bankCorrAccount: formData.bankCorrAccount.trim() || undefined,
            bankBik: formData.bankBik.trim() || undefined,
            email: formData.email.trim() || undefined,
            directorName: formData.directorName.trim() || undefined
          }
        }).unwrap();
        onCarrierCreated?.(updatedCarrier);
      } else {
        const newCarrier = await createCarrier({
          name: formData.name.trim(),
          contactPerson: formData.contactPerson.trim() || undefined,
          phone: formData.phone.trim() || undefined,
          inn: formData.inn.trim() || undefined,
          legalAddress: formData.legalAddress.trim() || undefined,
          postalAddress: formData.postalAddress.trim() || undefined,
          kpp: formData.kpp.trim() || undefined,
          ogrn: formData.ogrn.trim() || undefined,
          bankName: formData.bankName.trim() || undefined,
          bankAccount: formData.bankAccount.trim() || undefined,
          bankCorrAccount: formData.bankCorrAccount.trim() || undefined,
          bankBik: formData.bankBik.trim() || undefined,
          email: formData.email.trim() || undefined,
          directorName: formData.directorName.trim() || undefined
        }).unwrap();
        onCarrierCreated?.(newCarrier);
      }

      onClose();
      setFormData({
        name: '',
        contactPerson: '',
        phone: '',
        inn: '',
        legalAddress: '',
        postalAddress: '',
        kpp: '',
        ogrn: '',
        bankName: '',
        bankAccount: '',
        bankCorrAccount: '',
        bankBik: '',
        email: '',
        directorName: ''
      });
      setErrors({});
    } catch (error: any) {
      setErrors({ general: error.data?.message || 'Ошибка при создании/обновлении перевозчика' });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Заполняем форму данными для редактирования
  useEffect(() => {
    if (isOpen && carrier) {
      setFormData({
        name: carrier.name,
        contactPerson: carrier.contactPerson || '',
        phone: carrier.phone || '',
        inn: carrier.inn || '',
        legalAddress: (carrier as any).legalAddress || '',
        postalAddress: (carrier as any).postalAddress || '',
        kpp: (carrier as any).kpp || '',
        ogrn: (carrier as any).ogrn || '',
        bankName: (carrier as any).bankName || '',
        bankAccount: (carrier as any).bankAccount || '',
        bankCorrAccount: (carrier as any).bankCorrAccount || '',
        bankBik: (carrier as any).bankBik || '',
        email: (carrier as any).email || '',
        directorName: (carrier as any).directorName || ''
      });
    } else if (isOpen && !carrier) {
      setFormData({
        name: '',
        contactPerson: '',
        phone: '',
        inn: '',
        legalAddress: '',
        postalAddress: '',
        kpp: '',
        ogrn: '',
        bankName: '',
        bankAccount: '',
        bankCorrAccount: '',
        bankBik: '',
        email: '',
        directorName: ''
      });
    }
    setErrors({});
  }, [isOpen, carrier]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold mb-4">
          {isEditMode ? 'Редактировать перевозчика' : 'Создать перевозчика'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Наименование перевозчика *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Введите наименование перевозчика"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Контактное лицо
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) => handleInputChange('contactPerson', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите ФИО контактного лица"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Телефон
            </label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите номер телефона"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ИНН
            </label>
            <input
              type="text"
              value={formData.inn}
              onChange={(e) => handleInputChange('inn', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите ИНН перевозчика"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Юридический адрес
            </label>
            <input
              type="text"
              value={formData.legalAddress}
              onChange={(e) => handleInputChange('legalAddress', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите юридический адрес"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Почтовый адрес
            </label>
            <input
              type="text"
              value={formData.postalAddress}
              onChange={(e) => handleInputChange('postalAddress', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите почтовый адрес"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">КПП</label>
              <input type="text" value={formData.kpp} onChange={(e)=>handleInputChange('kpp', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="КПП" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ОГРН</label>
              <input type="text" value={formData.ogrn} onChange={(e)=>handleInputChange('ogrn', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ОГРН" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Банк</label>
            <input type="text" value={formData.bankName} onChange={(e)=>handleInputChange('bankName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Наименование банка" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">р/с</label>
              <input type="text" value={formData.bankAccount} onChange={(e)=>handleInputChange('bankAccount', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Расчетный счет" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">к/с</label>
              <input type="text" value={formData.bankCorrAccount} onChange={(e)=>handleInputChange('bankCorrAccount', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Корреспондентский счет" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">БИК</label>
              <input type="text" value={formData.bankBik} onChange={(e)=>handleInputChange('bankBik', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="БИК" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input type="email" value={formData.email} onChange={(e)=>handleInputChange('email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Электронная почта" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Генеральный директор</label>
              <input type="text" value={formData.directorName} onChange={(e)=>handleInputChange('directorName', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ФИО руководителя" />
            </div>
          </div>

          {errors.general && (
            <div className="text-red-500 text-sm">{errors.general}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (isEditMode ? 'Обновление...' : 'Создание...') : (isEditMode ? 'Обновить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCarrierModal; 